import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc,
  onSnapshot,
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  getDocs,
  Timestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from './firebase';
import { logDiscountUsage, getUserUsageCount } from './discountUsageService';

// Discount Code Interface
export interface DiscountCode {
  id: string;
  code: string;
  description: string;
  type: 'percentage' | 'fixed' | 'free_shipping';
  value: number; // percentage (0-100) or fixed amount
  minOrderAmount: number;
  maxUses: number;
  currentUses: number;
  status: 'active' | 'inactive' | 'expired';
  startDate: Date;
  endDate: Date;
  applicableProducts: string[]; // product categories or 'all'
  affiliateId?: string; // Link to affiliate for commission tracking
  membersOnly?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Discount Config Interface
export interface DiscountConfig {
  discountCodes: DiscountCode[];
  lastUpdated?: Date;
}

/**
 * Get discount configuration from Firestore
 */
export const getDiscountConfig = async (): Promise<DiscountConfig> => {
  try {
    const discountRef = doc(db, 'config', 'discounts');
    const discountDoc = await getDoc(discountRef);
    
    if (!discountDoc.exists()) {
      return { discountCodes: [] };
    }
    
    const data = discountDoc.data();
    const discountCodes: DiscountCode[] = [];
    
    if (data.discountCodes && Array.isArray(data.discountCodes)) {
      data.discountCodes.forEach((discount: any) => {
        discountCodes.push({
          id: discount.id || `discount-${Date.now()}`,
          code: discount.code || '',
          description: discount.description || '',
          type: discount.type || 'percentage',
          value: discount.value || 0,
          minOrderAmount: discount.minOrderAmount || 0,
          maxUses: discount.maxUses || 100,
          currentUses: discount.currentUses || 0,
          status: discount.status || 'active',
          startDate: discount.startDate?.toDate ? discount.startDate.toDate() : new Date(),
          endDate: discount.endDate?.toDate ? discount.endDate.toDate() : new Date(),
          applicableProducts: discount.applicableProducts || ['all'],
          affiliateId: discount.affiliateId || undefined,
          membersOnly: !!discount.membersOnly,
          createdAt: discount.createdAt?.toDate ? discount.createdAt.toDate() : new Date(),
          updatedAt: discount.updatedAt?.toDate ? discount.updatedAt.toDate() : new Date()
        });
      });
    }
    
    return {
      discountCodes: discountCodes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
    };
  } catch (error) {
    console.error('Error fetching discount config:', error);
    return { discountCodes: [] };
  }
};

/**
 * Update discount configuration in Firestore
 */
export const updateDiscountConfig = async (config: DiscountConfig): Promise<void> => {
  try {
    const discountRef = doc(db, 'config', 'discounts');
    const updateData = {
      discountCodes: config.discountCodes.map(discount => {
        const discountData: any = {
          id: discount.id,
          code: discount.code,
          description: discount.description,
          type: discount.type,
          value: discount.value,
          minOrderAmount: discount.minOrderAmount,
          maxUses: discount.maxUses,
          currentUses: discount.currentUses,
          status: discount.status,
          startDate: discount.startDate,
          endDate: discount.endDate,
          applicableProducts: discount.applicableProducts,
          membersOnly: !!discount.membersOnly,
          createdAt: discount.createdAt,
          updatedAt: new Date()
        };
        
        // Only include affiliateId if it has a value
        if (discount.affiliateId) {
          discountData.affiliateId = discount.affiliateId;
        }
        
        return discountData;
      }),
      lastUpdated: new Date()
    };
    
    await setDoc(discountRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating discount config:', error);
    throw error;
  }
};

/**
 * Subscribe to discount configuration changes
 */
export const subscribeToDiscountConfig = (
  callback: (config: DiscountConfig) => void
): (() => void) => {
  const discountRef = doc(db, 'config', 'discounts');
  
  return onSnapshot(discountRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const discountCodes: DiscountCode[] = [];
      
      if (data.discountCodes && Array.isArray(data.discountCodes)) {
        data.discountCodes.forEach((discount: any) => {
          discountCodes.push({
            id: discount.id || `discount-${Date.now()}`,
            code: discount.code || '',
            description: discount.description || '',
            type: discount.type || 'percentage',
            value: discount.value || 0,
            minOrderAmount: discount.minOrderAmount || 0,
            maxUses: discount.maxUses || 100,
            currentUses: discount.currentUses || 0,
            status: discount.status || 'active',
            startDate: discount.startDate?.toDate ? discount.startDate.toDate() : new Date(),
            endDate: discount.endDate?.toDate ? discount.endDate.toDate() : new Date(),
            applicableProducts: discount.applicableProducts || ['all'],
          affiliateId: discount.affiliateId || undefined,
          membersOnly: !!discount.membersOnly,
          createdAt: discount.createdAt?.toDate ? discount.createdAt.toDate() : new Date(),
          updatedAt: discount.updatedAt?.toDate ? discount.updatedAt.toDate() : new Date()
        });
        });
      }
      
      callback({
        discountCodes: discountCodes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
      });
    } else {
      callback({ discountCodes: [] });
    }
  }, (error) => {
    console.error('Error subscribing to discount config:', error);
    callback({ discountCodes: [] });
  });
};

/**
 * Validate discount code for checkout
 */
export const validateDiscountCode = async (
  code: string, 
  userId: string, 
  orderTotal: number,
  userEmail: string
): Promise<{
  isValid: boolean;
  discount?: DiscountCode;
  discountAmount?: number;
  error?: string;
}> => {
  try {
    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === code.toUpperCase() && 
      d.status === 'active'
    );
    
    if (!discount) {
      return { isValid: false, error: 'Invalid discount code' };
    }
    
    // Check date validity
    const now = new Date();
    if (now < discount.startDate || now > discount.endDate) {
      return { isValid: false, error: 'Discount code has expired' };
    }
    
    // Check minimum order amount
    if (orderTotal < discount.minOrderAmount) {
      return { 
        isValid: false, 
        error: `Minimum order amount is $${discount.minOrderAmount.toFixed(2)}` 
      };
    }
    
    // Check usage limits
    if (discount.currentUses >= discount.maxUses) {
      return { isValid: false, error: 'Discount code usage limit reached' };
    }
    
    // Check user-specific usage (optional: implement per-user limits)
    const userUsageCount = await getUserUsageCount(discount.id, userId);
    const maxUsesPerUser = 1; // You can make this configurable per discount
    if (userUsageCount >= maxUsesPerUser) {
      return { isValid: false, error: 'You have already used this discount code' };
    }
    
    // Calculate discount amount
    let discountAmount = 0;
    switch (discount.type) {
      case 'percentage':
        discountAmount = (orderTotal * discount.value) / 100;
        break;
      case 'fixed':
        discountAmount = Math.min(discount.value, orderTotal);
        break;
      case 'free_shipping':
        discountAmount = 0; // This would be handled separately in shipping calculation
        break;
    }
    
    return {
      isValid: true,
      discount,
      discountAmount: Math.round(discountAmount * 100) / 100 // Round to 2 decimal places
    };
  } catch (error) {
    console.error('Error validating discount code:', error);
    return { isValid: false, error: 'Failed to validate discount code' };
  }
};

/**
 * Apply discount code atomically during order creation
 */
export const applyDiscountCodeAtomic = async (
  discountCode: string,
  orderId: string,
  userId: string,
  userEmail: string,
  orderTotal: number,
  discountAmount: number
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // Get current discount config
    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === discountCode.toUpperCase()
    );
    
    if (!discount) {
      throw new Error('Discount code not found');
    }
    
    // Update discount usage count
    const updatedDiscountCodes = config.discountCodes.map(d => 
      d.id === discount.id 
        ? { ...d, currentUses: d.currentUses + 1, updatedAt: new Date() }
        : d
    );
    
    const discountRef = doc(db, 'config', 'discounts');
    batch.set(discountRef, {
      discountCodes: updatedDiscountCodes.map(d => {
        const discountData: any = {
          ...d,
          startDate: d.startDate,
          endDate: d.endDate,
          createdAt: d.createdAt,
          updatedAt: d.updatedAt
        };
        
        // Only include affiliateId if it has a value
        if (d.affiliateId) {
          discountData.affiliateId = d.affiliateId;
        }
        
        return discountData;
      }),
      lastUpdated: new Date()
    }, { merge: true });
    
    // Log usage in separate collection
    const usageRef = collection(db, 'discount_usage');
    const usageDoc = doc(usageRef);
    const usageData: any = {
      discountCodeId: discount.id,
      discountCode: discount.code,
      orderId,
      userId,
      userEmail,
      discountAmount,
      orderTotal,
      usedAt: new Date(),
      ipAddress: '', // This would be passed from the frontend
      userAgent: '' // This would be passed from the frontend
    };
    
    // Only include affiliateId if it has a value
    if (discount.affiliateId) {
      usageData.affiliateId = discount.affiliateId;
    }
    
    batch.set(usageDoc, usageData);
    
    // Commit the batch
    await batch.commit();
    
    console.log('Discount code applied successfully:', discountCode);
  } catch (error) {
    console.error('Error applying discount code:', error);
    throw new Error('Failed to apply discount code');
  }
};

/**
 * Get discount code by code string (for customer app)
 */
export const getDiscountByCode = async (code: string): Promise<DiscountCode | null> => {
  try {
    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === code.toUpperCase() && 
      d.status === 'active'
    );
    
    return discount || null;
  } catch (error) {
    console.error('Error fetching discount by code:', error);
    return null;
  }
};

/**
 * Check if discount code is available for use
 */
export const isDiscountCodeAvailable = async (code: string): Promise<boolean> => {
  try {
    const discount = await getDiscountByCode(code);
    if (!discount) return false;
    
    const now = new Date();
    return (
      discount.status === 'active' &&
      now >= discount.startDate &&
      now <= discount.endDate &&
      discount.currentUses < discount.maxUses
    );
  } catch (error) {
    console.error('Error checking discount availability:', error);
    return false;
  }
};

/**
 * Add a new discount code
 */
export const addDiscountCode = async (discountData: Omit<DiscountCode, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
  try {
    const config = await getDiscountConfig();
    
    // Check if code already exists
    const existingCode = config.discountCodes.find(d => 
      d.code.toUpperCase() === discountData.code.toUpperCase()
    );
    
    if (existingCode) {
      throw new Error('Discount code already exists');
    }
    
  const newDiscount: DiscountCode = {
    ...discountData,
    id: `discount-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    membersOnly: !!discountData.membersOnly,
    createdAt: new Date(),
    updatedAt: new Date()
  };
    
    const updatedConfig: DiscountConfig = {
      discountCodes: [newDiscount, ...config.discountCodes],
      lastUpdated: new Date()
    };
    
    await updateDiscountConfig(updatedConfig);
  } catch (error) {
    console.error('Error adding discount code:', error);
    throw error;
  }
};

/**
 * Update an existing discount code
 */
export const updateDiscountCode = async (id: string, updates: Partial<Omit<DiscountCode, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
  try {
    const config = await getDiscountConfig();
    const discountIndex = config.discountCodes.findIndex(d => d.id === id);
    
    if (discountIndex === -1) {
      throw new Error('Discount code not found');
    }
    
    // If updating code, check for duplicates
    if (updates.code) {
      const existingCode = config.discountCodes.find(d => 
        d.id !== id && d.code.toUpperCase() === updates.code!.toUpperCase()
      );
      
      if (existingCode) {
        throw new Error('Discount code already exists');
      }
    }
    
    const updatedDiscountCodes = [...config.discountCodes];
    updatedDiscountCodes[discountIndex] = {
      ...updatedDiscountCodes[discountIndex],
      ...updates,
      updatedAt: new Date()
    };
    
    const updatedConfig: DiscountConfig = {
      discountCodes: updatedDiscountCodes,
      lastUpdated: new Date()
    };
    
    await updateDiscountConfig(updatedConfig);
  } catch (error) {
    console.error('Error updating discount code:', error);
    throw error;
  }
};

/**
 * Delete a discount code
 */
export const deleteDiscountCode = async (id: string): Promise<void> => {
  try {
    const config = await getDiscountConfig();
    const updatedDiscountCodes = config.discountCodes.filter(d => d.id !== id);
    
    if (updatedDiscountCodes.length === config.discountCodes.length) {
      throw new Error('Discount code not found');
    }
    
    const updatedConfig: DiscountConfig = {
      discountCodes: updatedDiscountCodes,
      lastUpdated: new Date()
    };
    
    await updateDiscountConfig(updatedConfig);
  } catch (error) {
    console.error('Error deleting discount code:', error);
    throw error;
  }
};

/**
 * Get discount code by ID
 */
export const getDiscountCodeById = async (id: string): Promise<DiscountCode | null> => {
  try {
    const config = await getDiscountConfig();
    return config.discountCodes.find(d => d.id === id) || null;
  } catch (error) {
    console.error('Error fetching discount code by ID:', error);
    return null;
  }
}; 
